/**
 * Promo Code URL Parameter Handler
 * Automatically applies promo codes from URL query parameters to the cart
 * Integrates with SlideCartHQ (AMP cart) and displays console notifications
 * Uses Shopify's discount endpoint for application
 */
(function() {
  'use strict';

  // Track if promo code has been processed to prevent duplicate applications
  let promoCodeProcessed = false;

  // Enable debug logging (set to false in production)
  const DEBUG = true;

  /**
   * Debug logging function
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   * @private
   */
  function log(message, data) {
    if (DEBUG) {
      console.log('[PromoCodeHandler]', message, data || '');
    }
  }

  /**
   * Get promo code from URL parameters
   * Supports 'promo', 'promo_code', and 'promo-code' parameters
   * @returns {string|null} - Promo code value or null if not found
   * @private
   */
  function getPromoCodeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    // Check all three parameter variations: 'promo', 'promo_code', and 'promo-code'
    const promoCode = urlParams.get('promo') || urlParams.get('promo_code') || urlParams.get('promo-code');
    
    log('Checking URL parameters:', {
      promo: urlParams.get('promo'),
      promo_code: urlParams.get('promo_code'),
      'promo-code': urlParams.get('promo-code'),
      found: promoCode
    });
    
    return promoCode;
  }

  /**
   * Remove promo code parameters from URL without page reload
   * Removes 'promo', 'promo_code', and 'promo-code' parameters
   * @private
   */
  function removePromoParametersFromURL() {
    const url = new URL(window.location.href);
    const hadPromo = url.searchParams.has('promo');
    const hadPromoCode = url.searchParams.has('promo_code');
    const hadPromoCodeHyphen = url.searchParams.has('promo-code');
    
    url.searchParams.delete('promo');
    url.searchParams.delete('promo_code');
    url.searchParams.delete('promo-code');
    
    window.history.replaceState({}, '', url);
    
    log('Removed promo parameters from URL', { hadPromo, hadPromoCode, hadPromoCodeHyphen });
  }

  /**
   * Update coupon input field to show the applied discount code
   * @param {string} discountCode - The discount code to display
   * @private
   */
  function updateCouponInputField(discountCode) {
    const couponInput = document.getElementById('cartCouponInput');
    const couponError = document.getElementById('cartCouponError');
    
    if (couponInput) {
      couponInput.value = discountCode;
      couponInput.style.color = '#4CAF50'; // Green color to indicate it's applied
      log('Updated coupon input field with code:', discountCode);
    }
    
    if (couponError) {
      couponError.textContent = `Discount code "${discountCode}" has been applied`;
      couponError.style.display = 'block';
      couponError.style.color = '#4CAF50'; // Green for success
      log('Updated coupon error/success message');
    }
  }

  /**
   * Refresh SlideCartHQ cart UI
   * Uses multiple methods to ensure compatibility
   * @private
   */
  function refreshSlideCartHQ() {
    log('Refreshing SlideCartHQ...');
    
    // Method 1: Use SlideCartHQ refresh method if available
    if (window.SlideCartHQ && typeof window.SlideCartHQ.refresh === 'function') {
      log('Using SlideCartHQ.refresh()');
      window.SlideCartHQ.refresh();
    } else {
      log('SlideCartHQ.refresh() not available');
    }
    
    // Method 2: Use SLIDECART_UPDATE callback if available
    if (typeof window.SLIDECART_UPDATE === 'function') {
      log('Using SLIDECART_UPDATE()');
      window.SLIDECART_UPDATE(function() {
        log('SlideCartHQ updated via SLIDECART_UPDATE');
      });
    } else {
      log('SLIDECART_UPDATE() not available');
    }
    
    // Method 3: Dispatch cart refresh events (used by cart-oatmilk-handler.js)
    log('Dispatching cart refresh events');
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: {},
      bubbles: true
    }));
    
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {},
      bubbles: true
    }));
  }

  /**
   * Check if discount code is in cart
   * @param {string} promoCode - The promo code to check for
   * @returns {Promise<boolean>} - True if discount is in cart
   * @private
   */
  async function checkDiscountInCart(promoCode) {
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        log('Failed to fetch cart:', cartResponse.status);
        return false;
      }
      
      const cart = await cartResponse.json();
      log('Cart data:', cart);
      
      // Check discount_codes array (array of strings)
      if (cart.discount_codes && Array.isArray(cart.discount_codes)) {
        const found = cart.discount_codes.some(code => 
          code && code.toLowerCase().trim() === promoCode.toLowerCase().trim()
        );
        if (found) {
          log('Discount found in cart.discount_codes');
          return true;
        }
      }
      
      // Check cart_level_discount_applications array (array of objects)
      if (cart.cart_level_discount_applications && Array.isArray(cart.cart_level_discount_applications)) {
        const found = cart.cart_level_discount_applications.some(discount => {
          const code = discount.code || discount.title || discount.discount_application?.title || '';
          return code.toLowerCase().trim() === promoCode.toLowerCase().trim();
        });
        if (found) {
          log('Discount found in cart.cart_level_discount_applications');
          return true;
        }
      }
      
      log('Discount not found in cart');
      return false;
    } catch (error) {
      log('Error checking cart for discount:', error);
      return false;
    }
  }

  /**
   * Apply promo code to cart using Shopify's discount endpoint
   * @param {string} promoCode - The promo code to apply
   * @returns {Promise<boolean>} - Success status
   * @private
   */
  async function applyPromoCode(promoCode) {
    log('Applying promo code:', promoCode);
    
    try {
      // Apply the discount to the cart using Shopify's discount endpoint
      // Use GET method and follow redirects (Shopify redirects on success)
      log('Applying discount via Shopify endpoint...');
      const discountResponse = await fetch(`/discount/${promoCode}`, {
        method: 'GET',
        redirect: 'follow' // Follow redirects to see final result
      });
      
      log('Discount endpoint response status:', discountResponse.status);
      log('Discount endpoint response URL:', discountResponse.url);

      // Give Shopify a moment to process the discount
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify the discount was applied by checking the cart
      const discountApplied = await checkDiscountInCart(promoCode);
      
      if (discountApplied) {
        // Update coupon input field to show the applied code
        updateCouponInputField(promoCode);

        // Refresh SlideCartHQ with a delay to ensure cart update is complete
        setTimeout(() => {
          refreshSlideCartHQ();
        }, 500);

        // Show success message
        log('Promo code applied successfully');
        console.log('[PromoCodeHandler] Success:', `${promoCode} promo code applied! Discount has been applied to your cart.`);
        
        return true;
      } else {
        log('Discount endpoint returned but discount not found in cart');
        log('Response status:', discountResponse.status);
        log('Response URL:', discountResponse.url);
        console.error('[PromoCodeHandler] Promo Code Error: This promo code is not valid or could not be applied.');
        return false;
      }
    } catch (error) {
      log('Error applying promo code:', error);
      console.error('[PromoCodeHandler] Error applying promo code:', error);
      console.error('[PromoCodeHandler] Promo Code Error: Failed to apply promo code. Please try again.');
      return false;
    }
  }

  /**
   * Process promo code from URL parameter
   * @private
   */
  async function processPromoCode() {
    log('processPromoCode called');
    
    // Prevent duplicate processing
    if (promoCodeProcessed) {
      log('Promo code already processed, skipping');
      return;
    }

    // Get promo code from URL (supports 'promo', 'promo_code', and 'promo-code')
    const promoCode = getPromoCodeFromURL();

    // If no promo parameter, exit early
    if (!promoCode) {
      log('No promo code found in URL');
      return;
    }

    log('Promo code found:', promoCode);

    // Mark as processed immediately to prevent duplicate runs
    promoCodeProcessed = true;
    const trimmedCode = promoCode.trim();
    log('Trimmed promo code:', trimmedCode);

    // Remove promo parameter from URL immediately (before processing)
    removePromoParametersFromURL();

    // Show loading message
    log('Applying promo code...');
    console.log('[PromoCodeHandler] Applying Promo Code: Please wait...');

    // Apply the promo code
    const success = await applyPromoCode(trimmedCode);
    log('Promo code application result:', success);

    // Reset processed flag on error so user can try again if needed
    if (!success) {
      promoCodeProcessed = false;
    }
  }

  /**
   * Initialize promo code handler
   * Waits for DOM to be ready before processing
   * @private
   */
  function init() {
    log('Initializing promo code handler');
    log('Document ready state:', document.readyState);
    log('Current URL:', window.location.href);
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      log('Waiting for DOMContentLoaded');
      document.addEventListener('DOMContentLoaded', () => {
        log('DOMContentLoaded fired');
        setTimeout(processPromoCode, 200); // Give scripts more time to load
      });
    } else {
      // DOM is already ready, but wait a bit for other scripts to initialize
      log('DOM already ready, scheduling processPromoCode');
      setTimeout(processPromoCode, 300); // Delay to ensure other scripts are loaded
    }
  }

  // Initialize on page load
  log('Script loaded, calling init()');
  init();
})();

